export const SUBSCRIPTION_TIERS = {
  STANDARD: {
    id: 'standard',
    name: 'Standard',
    price: 6.99,
    currency: 'GBP',
    downloads: 8,
    bespokeCredits: 0,
    summary: 'Explore our catalogue of ESL resources, including new lessons added each month',
    features: [
      '8 downloads per month',
      '3 student pathways',
      'Explore our catalogue of ESL resources, including new lessons added each month',
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_STANDARD_PRICE_ID,
  },
  PREMIUM: {
    id: 'premium',
    name: 'Premium',
    price: 14.99,
    currency: 'GBP',
    downloads: -1, // Unlimited
    bespokeCredits: 0,
    summary: 'Full access to our complete library of resources, including new lessons added each month',
    features: [
      'Unlimited downloads per month',
      'Unlimited student pathways',
      'Full access to our complete library of resources, including new lessons added each month',
      'Organise and manage saved resources',
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_PREMIUM_PRICE_ID,
  },
  PREMIUM_BESPOKE: {
    id: 'premium_bespoke',
    name: 'Premium Bespoke lesson',
    price: 21.99,
    currency: 'GBP',
    downloads: -1, // Unlimited
    bespokeCredits: 2,
    summary: 'Full access to our complete library of resources, including new lessons added each month',
    features: [
      '2 bespoke lesson requests',
      'Unlimited student pathways',
      'Unlimited downloads per month',
      'Full access to our complete library of resources, including new lessons added each month',
      'Organise and manage saved resources',
    ],
    stripePriceId: import.meta.env.VITE_STRIPE_PREMIUM_BESPOKE_PRICE_ID,
  },
};

export const BESPOKE_ADDONS = {
  SINGLE: {
    id: 'bespoke_single',
    credits: 1,
    price: 5.00,
    stripePriceId: import.meta.env.VITE_STRIPE_BESPOKE_SINGLE_PRICE_ID,
  },
  TRIPLE: {
    id: 'bespoke_triple',
    credits: 3,
    price: 10.00,
    stripePriceId: import.meta.env.VITE_STRIPE_BESPOKE_TRIPLE_PRICE_ID,
  },
};

export const RESOURCE_LEVELS = [
  'Beginner (A1)',
  'Elementary (A2)',
  'Pre-Intermediate (B1)',
  'Intermediate (B2)',
  'Upper-Intermediate (C1)',
  'Advanced (C2)',
];

export const RESOURCE_AGE_GROUPS = [
  'Young Learners (5-12)',
  'Teenagers (13-17)',
  'Adults (18+)',
  'Business Professionals',
];

export const RESOURCE_SKILLS = [
  'Grammar',
  'Vocabulary',
  'Speaking',
  'Listening',
  'Reading',
  'Writing',
  'Pronunciation',
  'Exam Preparation',
];

export const BESPOKE_TURNAROUND = 72; // hours
export const BESPOKE_REVISION_TURNAROUND = 72; // hours
export const BESPOKE_ROLLOVER_MONTHS = 3;
export const BESPOKE_PUBLIC_DELAY_DAYS = 5; // working days

// ===== STUDENT LEARNING PATHWAY BUILDER =====

export const CEFR_LEVELS = [
  { value: 'A1', label: 'A1 - Beginner' },
  { value: 'A2', label: 'A2 - Elementary' },
  { value: 'B1', label: 'B1 - Pre-Intermediate' },
  { value: 'B2', label: 'B2 - Intermediate' },
  { value: 'C1', label: 'C1 - Upper-Intermediate' },
  { value: 'C2', label: 'C2 - Advanced' },
];

export const PATHWAY_PHASES = [
  { id: 'foundation', name: 'Foundation', color: 'blue' },
  { id: 'skill_expansion', name: 'Skill Expansion', color: 'teal' },
  { id: 'controlled_fluency', name: 'Controlled Fluency', color: 'indigo' },
  { id: 'independent_performance', name: 'Independent Performance', color: 'green' },
];

export const LEARNING_GOALS = [
  { value: 'general_english', label: 'General English' },
  { value: 'conversational_fluency', label: 'Conversational Fluency' },
  { value: 'business_english', label: 'Business English' },
  { value: 'exam_prep', label: 'Exam Preparation' },
  { value: 'academic_english', label: 'Academic English' },
  { value: 'travel_english', label: 'Travel & Survival English' },
  { value: 'professional_development', label: 'Professional Development' },
];

// Goals that ask for the learner's work details or target exam in the pathway form
export const WORK_GOALS = ['business_english', 'professional_development'];
export const EXAM_GOALS = ['exam_prep'];

export const EXAM_TYPES = [
  'IELTS Academic',
  'IELTS General Training',
  'Cambridge B1 Preliminary (PET)',
  'Cambridge B2 First (FCE)',
  'Cambridge C1 Advanced (CAE)',
  'Cambridge C2 Proficiency (CPE)',
  'TOEFL iBT',
  'TOEIC Listening & Reading',
  'TOEIC Speaking & Writing',
  'Other',
];

// Discontinued by Cambridge outside China, so no longer offered. Older pathways can still hold one.
export const RETIRED_EXAMS = [
  'Cambridge B1 Business Preliminary',
  'Cambridge B2 Business Vantage',
  'Cambridge C1 Business Higher',
];

// Exams set at one level. IELTS, TOEFL and TOEIC have no level of their own, so they use the teacher's target.
const EXAM_LEVELS = {
  'Cambridge B1 Preliminary (PET)': 'B1',
  'Cambridge B2 First (FCE)': 'B2',
  'Cambridge C1 Advanced (CAE)': 'C1',
  'Cambridge C2 Proficiency (CPE)': 'C2',
  'Cambridge B1 Business Preliminary': 'B1',
  'Cambridge B2 Business Vantage': 'B2',
  'Cambridge C1 Business Higher': 'C1',
};

// Cambridge's guided learning hours from beginner, using the low end of each range
const LEVEL_HOURS = { A1: 90, A2: 180, B1: 350, B2: 500, C1: 700, C2: 1000 };

// A rough guide, not a prediction: null unless the plan is far short of the exam's level.
// Keep in step with examFit in functions/prompts/examGuides.js.
export const examFit = (profile, now = Date.now()) => {
  const requiredLevel = EXAM_LEVELS[profile?.examType] || profile?.targetLevel;
  const currentLevel = profile?.confirmedLevel;
  if (!profile?.examType || !(currentLevel in LEVEL_HOURS) || !(requiredLevel in LEVEL_HOURS)) return null;
  const hoursNeeded = LEVEL_HOURS[requiredLevel] - LEVEL_HOURS[currentLevel];
  if (hoursNeeded <= 0) return null;

  const weeksToExam = profile.examDate ? Math.max(0, (new Date(profile.examDate).getTime() - now) / (7 * 24 * 60 * 60 * 1000)) : Infinity;
  const hoursPlanned = Math.round(Math.min(courseWeeks(profile), weeksToExam) * (profile.weeklyFrequency || 2) * ((profile.lessonDuration || 60) / 60));
  const levels = Object.keys(LEVEL_HOURS);
  const levelsToClimb = levels.indexOf(requiredLevel) - levels.indexOf(currentLevel);
  const short = hoursPlanned < hoursNeeded * 0.25 || (levelsToClimb >= 2 && hoursPlanned < hoursNeeded * 0.5);
  return short ? { currentLevel, requiredLevel, hoursNeeded, hoursPlanned } : null;
};

export const OBSERVATION_SCALE = [
  { value: 1, label: '1 - Significant difficulty' },
  { value: 2, label: '2 - Below expectations' },
  { value: 3, label: '3 - Meets expectations' },
  { value: 4, label: '4 - Above expectations' },
  { value: 5, label: '5 - Exceptional' },
];

export const PATHWAY_TIMEFRAMES = [
  { value: 3, label: '3 months' },
  { value: 6, label: '6 months' },
  { value: 12, label: '12 months' },
];

// Custom course lengths are set in weeks (functions/index.js generatePathway enforces the same limits)
export const MAX_CUSTOM_WEEKS = 52;
export const MIN_PATHWAY_LESSONS = 4;

// Weeks in a pathway: a custom length, or preset months of 4 weeks
export const courseWeeks = (student) => (
  student?.timeframeWeeks > 0 ? student.timeframeWeeks : (student?.timeframe || 6) * 4
);

export const courseLengthLabel = (student) => {
  if (student?.timeframeWeeks > 0) return `${student.timeframeWeeks} ${student.timeframeWeeks === 1 ? 'week' : 'weeks'}`;
  const months = student?.timeframe || 6;
  return `${months} ${months === 1 ? 'month' : 'months'}`;
};

export const LESSON_DURATIONS = [
  { value: 45, label: '45 minutes' },
  { value: 60, label: '60 minutes' },
];

// Group classes often run longer than 1-to-1 lessons
export const GROUP_LESSON_DURATIONS = [
  ...LESSON_DURATIONS,
  { value: 90, label: '90 minutes' },
];

export const MAX_GROUP_LEARNERS = 40;

export const FREE_TRIAL_DAYS = 7;
export const FREE_TRIAL_TIER = 'standard';

export const ASSESSMENT_TOKEN_EXPIRY_DAYS = 7;
export const MAX_ASSESSMENT_ATTEMPTS = 3;
export const MAX_PATHWAY_REGENERATIONS_PER_MONTH = 3;
export const MAX_WRITING_SAMPLE_WORDS = 500;
export const PREMIUM_TIERS = ['premium', 'premium_bespoke', 'business'];

// Every tier that unlocks pathways (Business = school plans, see BUSINESS_PLAN)
export const ACCESS_TIERS = ['standard', ...PREMIUM_TIERS];

// Business tier for schools: a base price plus a band for the number of teachers.
// Keep in sync with functions/schools.js.
export const BUSINESS_PLAN = {
  basePrice: 50,
  trialDays: 7,
  monthlyTeacherCredits: 2,
  maxSelfServeTeachers: 20,
  bands: [
    { id: 'business_5', teacherLimit: 5, price: 30, label: 'Up to 5 teachers' },
    { id: 'business_8', teacherLimit: 8, price: 50, label: 'Up to 8 teachers' },
    { id: 'business_20', teacherLimit: 20, price: 100, label: 'Up to 20 teachers' },
  ],
  features: [
    'Unlimited pathways for 1-to-1 students and group classes',
    'Unlimited downloads from the full resource library',
    '2 bespoke lesson credits per teacher each month',
    'Invite teachers and see every class in one place',
  ],
};

// A school's monthly price for its band (0 if unknown)
export const businessMonthly = (bandId) => {
  const b = BUSINESS_PLAN.bands.find((x) => x.id === bandId);
  return b ? BUSINESS_PLAN.basePrice + b.price : 0;
};
